import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { onboardingOptions } from '@/mocks/onboarding';
import { categoryData } from '@/mocks/categories';

type Step = 1 | 2 | 3 | 4;

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [categoryStatuses, setCategoryStatuses] = useState<Record<string, string>>({});
  const [concern, setConcern] = useState('');

  const progress = ((step - 1) / 3) * 100;

  const toggleCategory = useCallback((value: string) => {
    setSelectedCategories((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  }, []);

  const setStatus = useCallback((category: string, status: string) => {
    setCategoryStatuses((prev) => ({ ...prev, [category]: status }));
  }, []);

  const canProceed = () => {
    switch (step) {
      case 1:
        return selectedCategories.length > 0;
      case 2:
        return selectedCategories.every((c) => categoryStatuses[c]);
      case 3:
        return concern !== '';
      case 4:
        return true;
    }
  };

  const getSummary = () => {
    const needGuide = selectedCategories.filter((c) =>
      ['not_started', 'browsing', 'criteria', 'too_many'].includes(categoryStatuses[c] || ''),
    );
    const hasCandidate = selectedCategories.filter((c) =>
      ['has_candidate', 'consultation', 'contracted'].includes(categoryStatuses[c] || ''),
    );

    const guideNames = needGuide.map((c) => categoryData[c as keyof typeof categoryData].name);
    const candidateNames = hasCandidate.map((c) => categoryData[c as keyof typeof categoryData].name);

    let text = '';
    if (guideNames.length > 0 && candidateNames.length > 0) {
      text = `${guideNames.join('과 ')}는 기준 정리가 필요하고, ${candidateNames.join('은 ')} 후보 비교 단계예요.`;
    } else if (guideNames.length > 0) {
      text = `${guideNames.join('과 ')}는 기준 정리가 필요해요.`;
    } else if (candidateNames.length > 0) {
      text = `${candidateNames.join('과 ')}는 후보 비교 단계예요.`;
    } else {
      text = '모든 항목의 기준 정리가 필요해요.';
    }
    return text;
  };

  return (
    <div className="min-h-[100dvh] bg-cream flex flex-col">
      <div className="px-5 pt-6 pb-4">
        <div className="flex items-center gap-3 mb-4">
          {step > 1 && (
            <button
              onClick={() => setStep((s) => (s - 1) as Step)}
              className="w-8 h-8 flex items-center justify-center text-navy cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl" />
            </button>
          )}
          <div className="flex-1 h-1.5 bg-cream-dark rounded-full overflow-hidden">
            <div
              className="h-full bg-navy rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-xs text-beige-dark font-medium">{step}/4</span>
        </div>
      </div>

      <div className="flex-1 px-5 pb-6 overflow-y-auto">
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <p className="text-softgold text-sm font-medium mb-2">STEP 1</p>
              <h2 className="text-navy text-xl font-bold leading-snug mb-2">
                어떤 항목을
                <br />
                준비하고 있나요?
              </h2>
              <p className="text-beige-dark text-sm">복수 선택 가능해요</p>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {onboardingOptions.categories.map((cat) => {
                const info = categoryData[cat.value as keyof typeof categoryData];
                const isSelected = selectedCategories.includes(cat.value);
                return (
                  <button
                    key={cat.value}
                    onClick={() => toggleCategory(cat.value)}
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200 text-left cursor-pointer ${
                      isSelected
                        ? 'border-navy bg-navy/5'
                        : 'border-cream-dark bg-white hover:border-beige'
                    }`}
                  >
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                        isSelected ? 'bg-navy text-white' : 'bg-cream text-navy'
                      }`}
                    >
                      <i className={`${cat.icon} text-xl`} />
                    </div>
                    <div className="flex-1">
                      <p className={`font-semibold ${isSelected ? 'text-navy' : 'text-gray-700'}`}>
                        {cat.label}
                      </p>
                      <p className="text-beige-dark text-xs mt-0.5">{info.description}</p>
                    </div>
                    {isSelected && (
                      <div className="w-6 h-6 rounded-full bg-navy flex items-center justify-center shrink-0">
                        <i className="ri-check-line text-white text-sm" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div>
              <p className="text-softgold text-sm font-medium mb-2">STEP 2</p>
              <h2 className="text-navy text-xl font-bold leading-snug mb-2">
                각 항목은
                <br />
                현재 어떤 단계인가요?
              </h2>
            </div>
            {selectedCategories.map((catValue) => {
              const catInfo = onboardingOptions.categories.find((c) => c.value === catValue);
              const data = categoryData[catValue as keyof typeof categoryData];
              return (
                <div key={catValue} className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-navy/10 flex items-center justify-center">
                      <i className={`${data.icon} text-navy text-sm`} />
                    </div>
                    <p className="font-semibold text-navy">{catInfo?.label}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {onboardingOptions.categoryStatuses.map((status) => {
                      const isSelected = categoryStatuses[catValue] === status.value;
                      return (
                        <button
                          key={status.value}
                          onClick={() => setStatus(catValue, status.value)}
                          className={`px-3 py-3 rounded-xl text-sm font-medium border-2 transition-all cursor-pointer whitespace-nowrap ${
                            isSelected
                              ? 'border-navy bg-navy text-white'
                              : 'border-cream-dark bg-white text-gray-600 hover:border-beige'
                          }`}
                        >
                          {status.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div>
              <p className="text-softgold text-sm font-medium mb-2">STEP 3</p>
              <h2 className="text-navy text-xl font-bold leading-snug mb-2">
                가장 걱정되는 것은
                <br />
                무엇인가요?
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {onboardingOptions.concerns.map((c) => {
                const isSelected = concern === c.value;
                return (
                  <button
                    key={c.value}
                    onClick={() => setConcern(c.value)}
                    className={`p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${
                      isSelected
                        ? 'border-navy bg-navy/5'
                        : 'border-cream-dark bg-white hover:border-beige'
                    }`}
                  >
                    <p className={`font-medium ${isSelected ? 'text-navy' : 'text-gray-700'}`}>
                      {c.label}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6">
            <div>
              <p className="text-softgold text-sm font-medium mb-2">준비 완료</p>
              <h2 className="text-navy text-xl font-bold leading-snug mb-2">
                지혜님,
                <br />
                시작해 볼까요?
              </h2>
            </div>
            <div className="bg-white rounded-2xl p-5 border border-cream-dark">
              <p className="text-sm text-beige-dark mb-3">현재 상태 요약</p>
              <p className="text-navy font-medium text-base leading-relaxed">{getSummary()}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {selectedCategories.map((c) => (
                  <span
                    key={c}
                    className="px-3 py-1.5 rounded-full bg-cream text-navy text-xs font-medium border border-cream-dark"
                  >
                    {categoryData[c as keyof typeof categoryData].name}
                  </span>
                ))}
              </div>
            </div>
            <div className="bg-navy rounded-2xl p-5 text-white">
              <p className="text-sm text-cream-dark mb-1">다음 단계</p>
              <p className="font-medium text-base">각 항목별 기준을 정리하고 맞는 업체를 추천해드려요</p>
            </div>
          </div>
        )}
      </div>

      <div className="px-5 pb-[env(safe-area-inset-bottom)] pt-2 bg-cream border-t border-cream-dark">
        <button
          onClick={() => {
            if (step < 4) {
              setStep((s) => (s + 1) as Step);
            } else {
              navigate('/home');
            }
          }}
          disabled={!canProceed()}
          className={`w-full py-4 rounded-xl font-semibold text-base transition-all duration-200 whitespace-nowrap cursor-pointer ${
            canProceed()
              ? 'bg-navy text-white hover:bg-navy-light'
              : 'bg-cream-dark text-beige-dark cursor-not-allowed'
          }`}
        >
          {step === 4 ? '홈으로 이동' : '다음'}
        </button>
      </div>
    </div>
  );
}